import { saveAs as fileSaver } from 'file-saver';
import {
    rawRequest, createApiCaller,
    getFilename, checkContentType, contentTypeJson, contentTypeErrorJson,
    UnknownError, request,
} from '@factorin/rest';
import { API, config } from '@factorin/container.macro';


const saveAsFile = (response, filename) =>
    fileSaver(response.data, filename);


const readBlobJson = blob => new Promise((resolve, reject) => {
    const reader = new window.FileReader();
    reader.onload = () => {
        try {
            resolve(JSON.parse(reader.result));
        } catch (err) {
            reject(err);
        }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
});

const webApiCaller = createApiCaller(({ headers }) => ({
    basePath: config.basePath,
    apiPath: config.apiPath,
    headers: {
        'Accept-Language': window.localStorage.getItem('language'),
        ...API.state.requestHeaders,
        ...headers,
    },
}));

const getResponseAsJSON = async response => {
    let json = (checkContentType(response, contentTypeErrorJson) ||
        checkContentType(response, contentTypeJson)) && response.data;
    if (json instanceof window.Blob && json.type === contentTypeJson)
        json = await readBlobJson(json);
    return json;
};

/** @type { (request: any, args: { saveAs?: boolean, [rest: string]: any; }) } */
export const wrapRequest = async (request, { saveAs, ...args } = {}) => {
    try {
        const response = await request(args);
        const data = await getResponseAsJSON(response) || response.data;
        switch (response.status) {
            case 200:
            case 201:
            case 202:
            case 304:
                if (saveAs) {
                    return saveAsFile(
                        response,
                        getFilename(response.headers, saveAs),
                    );
                }
                return data;
            case 204:
                return undefined;
            default:
                throw new UnknownError(response);
        }
    } catch (error) {
        const json = await getResponseAsJSON(error.response);
        switch (error.response.status) {
            case 413:
                throw new Error(API.errorFormatter('tooLarge'));
            case 400:
            case 404:
            case 500:
                if (json && json.title) {
                    error.message = json.title;
                    break;
                }
            // falls through
            default:
                error.message = API.errorFormatter(error.message) || error.message;
                if (error instanceof UnknownError && error.response)
                    error.message += ` (HTTP ${error.response.status})`;
                break;
        }
        throw error;
    }
};

export const wrapApi = (func, ...args) => func(...args);

export const callApi = (apiCall, callArgs) =>
    wrapRequest(args => webApiCaller(apiCall, args), callArgs);

const method = method => args => callApi(request, { method, ...args });

export const get = method('GET');
export const post = method('POST');
export const put = method('PUT');
export const patch = method('PATCH');
export const remove = method('DELETE');

export const getFromPublicDir = (path, options = {}) => wrapRequest(rawRequest, {
    method: 'GET',
    url: `${config.basePath || ''}${config.pathPrefix || ''}/${path}`,
    ...options,
});
